"use client";
import React, { useEffect, useState } from "react";
import { useParams, useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { formatIndianCurrency } from "@/app/utils/helper";
import {
    BULLION,
    BULLION_INVESTMENT_TYPE_KEY,
    BULLION_KEY,
    BULLION_PRODUCT,
    BullionInvestmentType,
    PRODUCT_KEY,
    PRODUCT_WEIGHT,
    ProductDataResponse,
} from "@/app/alts/bullion/type-constants";
import { BullionProduct, getBullionProducts } from "@/app/api/service/bullion";
import useMobile from "@/app/hooks/use-mobile";
import AssetInformation from "./components/asset-information";
import AssetInformationTitle from "./components/asset-information-title";
import OneTimeCalc from "./components/one-time-calc";
import SIPCalc from "./components/sip-calc";
import SkeletonCalculator from "./components/skeleton/skeleton-calculator";
import DigitalVsPhysical from "./components/digital-vs-physical";
import WhyICMGold from "./components/why-icm-gold";
import HowToInvest from "./components/how-to-invest";
import AllNeedToKnow from "./components/all-need-to-know";
import Breadcrumb, { type BreadcrumbPath } from "@/app/components/breadcrumb.client";
import ICMWrapper from "@/app/components/icm-wrapper";
import Testimonial from "@/app/components/testimonial";
import BlogsSection from "@/app/components/blogs-section";
import Faq from "@/app/components/faq";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import FdAppDrawer from "@/app/alts/fd/components/fd-app-drawer";
import { ProductFaqData } from "@/types/faq.model";
import { useAuth } from "@/context/authcontext";
import { DirectPaths } from "@/config/routes";

interface BullionPageClientProps {
    initialFaqData?: ProductFaqData | null;
}

function resolveBullionKey(type: string | undefined): BULLION_KEY {
    const normalized = type?.toLowerCase();
    if (normalized === "gold") return "GOLD";
    if (normalized === "silver") return "SILVER";
    return "GOLD";
}

const BullionPageClient = ({ initialFaqData }: BullionPageClientProps) => {
    const router = useRouter();
    const { isAuthenticated } = useAuth();
    const { isMobile } = useMobile();
    const [isIcmAppModal, setIsIcmAppModal] = useState(false);

    const handleDownloadAppClick = () => {
        // Always open branch link regardless of login status (consistent behavior)
        window.open(process.env.APP_BRANCH_LINK_BULLION, "_blank");
    };

    const params = useParams<{ type: string; id: string }>();
    const [activeBullion] = useState<BULLION_KEY>(() => resolveBullionKey(params.type));
    const [productsData, setProductsData] = useState<ProductDataResponse | null>(null);
    const [products, setProducts] = useState<BullionProduct[] | null>(null);
    const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
    const [activeTab, setactiveTab] = useState<BULLION_INVESTMENT_TYPE_KEY>(
        BullionInvestmentType.ONETIME,
    );

    useEffect(() => {
        const getBullionProduct = async () => {
            const products = await getBullionProducts();

            if (products && products.success) {
                setProducts(products?.data?.products || []);
                BULLION[activeBullion].CODE = products?.data?.products.find((item) => {
                    if (item.name.toUpperCase() === BULLION_PRODUCT[activeBullion].TYPE) {
                        return item;
                    }
                })?.code as PRODUCT_KEY;

                setLoadingProducts(false);
            } else {
                setLoadingProducts(true);
                console.error(
                    "Error fetching bullion products:",
                    products?.message || "Unknown error",
                );
            }
        };
        getBullionProduct();
    }, [activeBullion]);

    const getTabContent = (activeTab: BULLION_INVESTMENT_TYPE_KEY) => {
        switch (activeTab) {
            case BullionInvestmentType.ONETIME:
                return <OneTimeCalc type={activeBullion} setProductsData={setProductsData} />;
            case BullionInvestmentType.SIP:
                return <SIPCalc type={activeBullion} setProductsData={setProductsData} />;
            default:
                return null;
        }
    };

    const heading =
        activeBullion === BULLION_PRODUCT.GOLD.TYPE
            ? "How to buy Digital Gold from InCred Money"
            : activeBullion === BULLION_PRODUCT.SILVER.TYPE
              ? "How to buy Pure Digital Silver from InCred Money"
              : "";

    const bullionBreadcrumbPaths: BreadcrumbPath[] = [
        {
            label: "Home",
            route: isAuthenticated
                ? `${process.env.ICM_POST_URL}` /* `${process.env.ICM_BULLION_POST_URL}${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}` */
                : "/",
        },
        {
            label: activeBullion === BULLION_PRODUCT.GOLD.TYPE ? "24 karat gold" : "Silver",
        },
    ];

    return (
        <div id="section-bullion-page">
            <ICMWrapper>
                <Breadcrumb paths={bullionBreadcrumbPaths} />
                <div
                    id="section-gold-silver"
                    className={
                        "-mx-1 mb-s11 mt-s8 flex flex-col gap-12 md:mx-1 md:mt-s15 md:flex-row " +
                        (products ? "md:mb-[120px]" : "md:mb-[60px]")
                    }
                >
                    <div
                        className={
                            "flex flex-col " +
                            (products ? "md:w-[calc(100%-421px-48px)]" : "w-full")
                        }
                    >
                        <div className="mb-s8 flex gap-[19.432px] md:mb-8">
                            {loadingProducts ? (
                                <>
                                    {[...Object.keys(BULLION_PRODUCT)].map((product) => {
                                        return (
                                            <button
                                                className="flex h-[44.5px] w-full animate-pulse items-center justify-center rounded-[23px] border border-neutral-100 bg-neutral-100 text-base font-semibold md:max-w-[180px]"
                                                disabled
                                                key={String(product)}
                                            >
                                                &nbsp;
                                            </button>
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    {Object.entries(BULLION_PRODUCT).map((product) => {
                                        return (
                                            <button
                                                key={product[1].TYPE}
                                                id={`btn-${product[1].TYPE.toLowerCase()}`}
                                                onClick={() => {
                                                    if (
                                                        product[1].TYPE ===
                                                        BULLION_PRODUCT.GOLD.TYPE
                                                    ) {
                                                        router.push(
                                                            `${isAuthenticated ? process.env.ICM_BULLION_POST_URL : process.env.ICM_BULLION_URL}${DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.GOLD}`,
                                                        );
                                                    } else if (
                                                        product[1].TYPE ===
                                                        BULLION_PRODUCT.SILVER.TYPE
                                                    ) {
                                                        router.push(
                                                            `${isAuthenticated ? process.env.ICM_BULLION_POST_URL : process.env.ICM_BULLION_URL}${DirectPaths.BULLION_POSTFIX_PATH + DirectPaths.SILVER}`,
                                                        );
                                                    }
                                                }}
                                                className={`flex h-[44.5px] w-full items-center justify-center rounded-[6.48px] border border-neutral-100 text-base font-semibold text-neutral-300 md:max-w-[180px] ${activeBullion === product[1].TYPE ? "bg-muted-ochre-50 text-neutral-900" : ""}`}
                                            >
                                                {product[1].TYPE === BULLION_PRODUCT.GOLD.TYPE
                                                    ? "Digital Gold"
                                                    : "Digital Silver"}
                                            </button>
                                        );
                                    })}{" "}
                                </>
                            )}
                        </div>

                        {/* H1 always in DOM for SEO (visible in page source); product from URL params */}
                        <AssetInformationTitle product={activeBullion} />

                        {!products || products?.length <= 0 ? (
                            <div
                                id="section-chart"
                                className="-mb-12 flex h-full min-h-[350px] items-center justify-center md:min-h-[355px]"
                            >
                                {!loadingProducts ? (
                                    <div className="h-3 w-[40%] animate-pulse rounded-[20px] bg-neutral-100"></div>
                                ) : (
                                    <p className="text-neutral-500" />
                                )}
                            </div>
                        ) : (
                            <>
                                {products && (
                                    <div id="section-chart" className="md:min-h-[455px]">
                                        <AssetInformation
                                            product={activeBullion}
                                            showTitle={false}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <>
                        {!loadingProducts ? (
                            <div
                                id="section-calculator"
                                className="max-w-[421px] rounded-l14 border border-neutral-100 md:min-w-[421px]"
                            >
                                <div className="flex items-end justify-between border-b border-neutral-100 px-s9 py-s7 md:px-s9 md:py-4">
                                    <div id="block-price-info">
                                        <p
                                            id="buying-price-per-gram"
                                            className="mb-1 text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-500"
                                        >
                                            Buying price (per {PRODUCT_WEIGHT.GRAM})
                                        </p>
                                        <h2
                                            id="buying-price-value"
                                            className="text-xl font-semibold leading-[130%] tracking-[-0.24px] md:text-3xl"
                                        >
                                            {productsData?.data && productsData.data?.live_price
                                                ? formatIndianCurrency(
                                                      productsData.data?.live_price,
                                                  )
                                                : "--"}
                                        </h2>
                                    </div>
                                    <p
                                        id="block-animation-live"
                                        className="flex items-center text-xs leading-[140%] text-error-600"
                                    >
                                        <span className="relative mr-1 flex size-2">
                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-500 opacity-75"></span>
                                            <span className="relative inline-flex size-2 rounded-full bg-error-600"></span>
                                        </span>
                                        LIVE
                                    </p>
                                </div>

                                <div
                                    id="block-onetime-sip"
                                    className="p-s7 pb-4 md:px-s9 md:pb-3 md:pt-6"
                                >
                                    <div className="flex gap-2 rounded-[12px] bg-neutral-50 p-1">
                                        {Object.keys(BullionInvestmentType).map((type) => (
                                            <Button
                                                key={type}
                                                id={"btn-" + type.toLowerCase()}
                                                onClick={() =>
                                                    setactiveTab(type as BullionInvestmentType)
                                                }
                                                className={`flex h-[44px] w-full cursor-pointer items-center justify-center rounded-[10px] bg-transparent !shadow-none hover:bg-inherit focus-visible:ring-1 focus-visible:ring-offset-1 ${activeTab === type ? "bg-white text-neutral-900 shadow-md hover:bg-white" : "text-neutral-500"}`}
                                            >
                                                {type === BullionInvestmentType.ONETIME
                                                    ? "1-time"
                                                    : "SIP"}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div id={"section-" + activeTab}>{getTabContent(activeTab)}</div>

                                <div className="hidden px-s9 pb-s9 md:block">
                                    <RegularButton
                                        id="btn-download-app"
                                        label="Download the app"
                                        variant={ButtonVariant.PrimaryDefault}
                                        size={ButtonSize.Large}
                                        noIcon={true}
                                        showRightIcon={false}
                                        className="min-w-full"
                                        onClick={handleDownloadAppClick}
                                    />
                                </div>
                            </div>
                        ) : (
                            <SkeletonCalculator />
                        )}
                    </>
                </div>

                <DigitalVsPhysical activeBullion={activeBullion} />

                <WhyICMGold
                    activeBullion={activeBullion}
                    showDownloadAppBtn={true}
                    handleDownloadAppClick={handleDownloadAppClick}
                />
            </ICMWrapper>

            <HowToInvest
                activeBullion={activeBullion}
                handleDownloadAppClick={handleDownloadAppClick}
                heading={heading}
            />

            {activeBullion === BULLION_PRODUCT["GOLD"].TYPE ? (
                <AllNeedToKnow activeBullion={activeBullion} />
            ) : (
                <div className="mt-4">&nbsp;</div>
            )}

            <ICMWrapper>
                <Testimonial sectionClassNames="pb-s11 pt-s11 px-6" />
            </ICMWrapper>

            <BlogsSection
                showDownloadAppBtn={true}
                handleDownloadAppClick={handleDownloadAppClick}
            />

            <Faq
                compact={false}
                bgColor="white"
                product="bullion"
                subProduct="generic"
                initialFaqData={initialFaqData}
            />

            {isMobile && (
                <div id="mobile-bottom-download-app" className="z-1 fixed bottom-0 left-0 w-full">
                    <div className="w-full bg-white px-4 py-3 shadow-md">
                        <div className="container mx-auto flex items-center justify-center">
                            <button
                                id="btn-download-app"
                                className="w-full rounded-full bg-orange-500 py-3 text-center font-medium text-white disabled:bg-orange-200"
                                onClick={handleDownloadAppClick}
                            >
                                Download the app
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isIcmAppModal && (
                <>
                    {isMobile ? (
                        <FdAppDrawer
                            isOpen={true}
                            onClose={() => {
                                setIsIcmAppModal(false);
                            }}
                        />
                    ) : (
                        <GetICMAppModal
                            open={true}
                            onClose={() => {
                                setIsIcmAppModal(false);
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default BullionPageClient;
